import { useCallback, useState } from "react";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

// MUI
import { Typography } from "@mui/material";

// Hooks
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";

// Components
import RequestReviewDialog from "./RequestReviewDialog";

// Types
import { IUpdateCommissionCycleStatus } from "@/Washouts/types/PayrollClosing/CommissionCycleClosingSection";
import { IUpdateCommissionCycleStatus as IUpdateCommissionCycleStatusResponse } from "@/Washouts/types/PayrollClosing/mutations";
import { ICommissionCycle, IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";
import moment from "moment";
import { or, ternaryResolver } from "@/Common/helpers/sonar";
import { TInput, TTwoSelectedBlockPosition } from "@/Washouts/types/PositionsDialog";
import { LoadingButton } from "@mui/lab";
import { useLocation } from "@/Common/hooks/Location/useLocation";
import { getDateByTimeZone } from "@dealerwave/dw-layer-date-converter";

interface IRequestReviewButtonProps {
	requesting: boolean;
	commissionCycle: ICommissionCycle | undefined;
	payPeriod?: IPayPeriod;
	partnerPayPeriod?: IPayPeriod | null;
	disabledPositions: string[] | undefined;
	disabledPartnerPositions: string[] | undefined;
	positionStatuses: IStatus[] | undefined;
	updateCycleStatus: UseMutateAsyncFunction<IUpdateCommissionCycleStatusResponse | null | undefined, Error, IUpdateCommissionCycleStatus>;
}

type TIsAfterEndPayPeriodArgs = {
	payPeriod: NonNullable<IPayPeriod>;
	partnerPayPeriod?: NonNullable<IPayPeriod>;
	commissionCycle: NonNullable<ICommissionCycle>;
	commissionCycles: ICommissionCycle[];
	timezone: string;
};

type TUpdateStatusCommissionCycleArgs = {
	payPeriodId: string;
	commissionCycleId: string;
	status: string;
	updateCycleStatus: UseMutateAsyncFunction<IUpdateCommissionCycleStatusResponse | null | undefined, Error, IUpdateCommissionCycleStatus>;
	positionStatuses: { position: string; status: "REVIEWING" }[];
};

/**
 *  Determine if the pay period is after the end of the commission cycle
 * @param args TIsAfterEndPayPeriodArgs
 * @returns  Boolean
 */
const isAfterEndCommissionCycle = (args: TIsAfterEndPayPeriodArgs) => {
	const { payPeriod, commissionCycle, commissionCycles, timezone } = args;
	// find the cycle we need to inspect
	const targetCycle = commissionCycles.find((c) => c._id === (payPeriod.previousCCId ?? commissionCycle._id));
	if (!targetCycle) {
		return false;
	}

	// determine if we should be "CLOSING"
	const lastPeriod = targetCycle.payPeriods.at(-1);
	const currentDate = getDateByTimeZone(timezone, moment(), { dateType: "Moment" });
	const targetCycleEndDay = moment(targetCycle.endDate).utc().endOf("day");
	return lastPeriod?._id === payPeriod._id && currentDate?.startOf("day").isAfter(targetCycleEndDay);
};

const updatePositionStatusInPayPeriod = async (args: TUpdateStatusCommissionCycleArgs) => {
	const { payPeriodId, status, updateCycleStatus, positionStatuses, commissionCycleId } = args;

	if (or(!commissionCycleId, !payPeriodId, !updateCycleStatus, !status)) {
		return;
	}

	const commissionCycleInput = {
		status,
		payPeriodId,
		commissionCycleId,
		positionStatus: positionStatuses,
	};

	await updateCycleStatus(commissionCycleInput);
};

const getPositionToReview = (selectedPositions: string[], positionStatuses: IStatus[]) => {
	return positionStatuses
		.filter((posStatus) => selectedPositions.some((pos) => pos.toLowerCase() === posStatus.position.toLowerCase()))
		.map(({ position }) => ({ position, status: "REVIEWING" }) as const);
};

const RequestReviewButton = (props: IRequestReviewButtonProps) => {
	const {
		requesting,
		commissionCycle,
		payPeriod,
		disabledPositions,
		positionStatuses,
		updateCycleStatus,
		partnerPayPeriod,
		disabledPartnerPositions,
	} = props;
	const [openPositionsSelector, setOpenPositionsSelector] = useState(false);

	const { commissionCycles } = useCommissionCyclesData();
	const { timezone } = useLocation();

	const reviewPositions = useCallback(
		async (input: TInput) => {
			const { selectedPositionStatus, selectedPartnerPositionStatus } = input as TTwoSelectedBlockPosition;

			if (!commissionCycle || !payPeriod) {
				return;
			}

			const payPeriodId = payPeriod._id;
			const commissionCycleId = commissionCycle._id;
			const payPeriodPosition = payPeriod?.positionStatus ?? [];
			const partnerPayPeriodPosition = partnerPayPeriod?.positionStatus ?? [];
			const isAfterEnd = isAfterEndCommissionCycle({ payPeriod, commissionCycle, commissionCycles, timezone });
			const isPartnerFirstPart = Boolean(payPeriod.nextCCId);
			const partnerCommissionCycleId = ternaryResolver(isPartnerFirstPart, payPeriod.nextCCId ?? "", payPeriod?.previousCCId ?? "");
			const closingStatus = ternaryResolver(Boolean(isAfterEnd) && !isPartnerFirstPart, "CLOSING", "ACTIVE");

			// map the partner positions we want to set to REVIEWING
			const partnerPositionsToReview = getPositionToReview(selectedPartnerPositionStatus, partnerPayPeriodPosition);

			const previousCommissionCycleInput = {
				payPeriodId,
				updateCycleStatus,
				commissionCycleId: partnerCommissionCycleId,
				status: closingStatus,
				positionStatuses: partnerPositionsToReview,
			};

			await updatePositionStatusInPayPeriod(previousCommissionCycleInput);

			// map the positions we want to set to REVIEWING
			const positionsToReview = getPositionToReview(selectedPositionStatus, payPeriodPosition);
			const currentStatus = ternaryResolver(Boolean(isAfterEnd) && isPartnerFirstPart, "CLOSING", "ACTIVE");

			const currentCommissionCycleInput = {
				payPeriodId,
				updateCycleStatus,
				commissionCycleId,
				status: currentStatus,
				positionStatuses: positionsToReview,
			};
			await updatePositionStatusInPayPeriod(currentCommissionCycleInput);
		},
		[commissionCycles, commissionCycle, payPeriod, partnerPayPeriod, updateCycleStatus]
	);

	if (!payPeriod || !commissionCycle) {
		return null;
	}

	const btnRequestReviewDisabled =
		requesting || (!!payPeriod?.nextCCId && !isAfterEndCommissionCycle({ payPeriod, commissionCycle, commissionCycles, timezone }));

	return (
		<>
			<LoadingButton
				id="select-plan-dialog-assign-btn"
				variant="contained"
				loading={requesting}
				disabled={btnRequestReviewDisabled}
				sx={{ minWidth: 151, minHeight: 30 }}
				aria-controls="select-plan-dialog-assign-btn-menu"
				className={"select-plan-dialog-assign-btn min-w-[151px]"}
				onClick={() => setOpenPositionsSelector(true)}
			>
				{!requesting && <Typography className="select-plan-dialog-assign-btn-text">Request Review</Typography>}
			</LoadingButton>
			<RequestReviewDialog
				commissionCycleId={commissionCycle?._id ?? ""}
				payPeriod={payPeriod}
				openPositionsSelector={openPositionsSelector}
				positionStatuses={positionStatuses}
				disabledPositions={disabledPositions}
				disabledPartnerPositions={disabledPartnerPositions}
				partnerPayPeriod={partnerPayPeriod}
				setOpenPositionsSelector={setOpenPositionsSelector}
				reviewPositions={reviewPositions}
			/>
		</>
	);
};

export default RequestReviewButton;
